<template>
  <v-navigation-drawer
    :value="active"
    fixed
    right
    temporary
    width="900px"
    touchless
    color="dark_backgroundBlack"
    @input="inputModal"
  >
    <div id="position-detail-drawer" class="pt-5 pb-3 pl-5 pr-2">
      <template v-if="position">
        <div class="position-detail-drawer-header px-4">
          <div class="items-flex justify-between">
            <div class="items-flex align-end">
              <h1 class="position-detail-drawer-ticker">
                {{ position.ticker }}
                <span v-if="position.name">({{ position.name }})</span>
              </h1>
              <div v-if="!isMobile" class="position-detail-drawer-tags ml-4">
                <TagGroup v-if="position.tags && position.tags.length" :tags="position.tags" />
              </div>
            </div>
            <div class="d-flex">
              <v-btn
                v-if="!position.pendingRecreate"
                class="elevation-0 ml-3"
                :class="{
                  'mr-1 px-1': isMobile,
                  'black--text': !$vuetify.theme.dark,
                  'white--text': $vuetify.theme.dark,
                }"
                small
                :color="$vuetify.theme.dark ? 'dark_backgroundGrey' : 'softBackgroundGrey'"
                @click="editPosition"
              >
                <v-icon size="18">mdi-pencil</v-icon>
                <span v-if="!isMobile" class="ml-2">
                  {{ this.$t(`views.portfolio.positionsTable.editPosition`) }}
                </span>
              </v-btn>
              <a @click="closeModal">
                <v-icon size="25">mdi-close</v-icon>
              </a>
            </div>
          </div>

          <h2
            class="position-detail-drawer-h2"
            :class="{
              'items-flex': !isMobile,
              wrap: !isMobile,
              'dark_Textgrey--text': $vuetify.theme.dark,
              'darkTextGrey--text': !$vuetify.theme.dark,
            }"
          >
            <div class="items-flex wrap">
              <CountryFlag
                :country="position.country"
                :legalCountry="position.legalCountry"
                :exchange="position.exchange"
                :exposureCountry="position.exposureCountry"
                :type="position.type"
                :width="23"
              />
              <div v-if="position.type" class="ml-2">
                <span class="drawer-bold">
                  {{ $t(`common.positionTypes.${position.type}`) }}
                </span>
                <span v-if="position.type === 'custom'" class="drawer-bold">
                  | {{ position.customType }}
                </span>
              </div>
              <div v-if="position.country && position.exchange" class="ml-1">
                <span class="drawer-bold">
                  - {{ $t(`common.exchanges.${position.country}.${position.exchange}`) }}
                </span>
              </div>
            </div>

            <template v-if="position.type !== 'cash'">
              <div v-if="!isMobile" class="mx-2">
                |
              </div>
              <div>
                {{ $t(`common.currency`) }}
                <span class="drawer-bold">{{ position.currency }}</span>
              </div>
            </template>
            <template v-if="position.sector">
              <div v-if="!isMobile" class="mx-2">
                |
              </div>
              <div>
                {{ position.sector }}
              </div>
            </template>
          </h2>

          <div v-if="isMobile" class="position-detail-drawer-tags mt-2">
            <TagGroup v-if="position.tags && position.tags.length" :tags="position.tags" />
          </div>

          <div v-if="position.notes" class="mt-2 position-detail-drawer-notes">
            {{ position.notes }}
          </div>
        </div>

        <div class="px-4 mt-2">
          <v-chip-group v-model="brokerSelected" column>
            <div v-for="brokerElem in positionBrokers" :key="brokerElem.broker._id">
              <TableBroker
                :broker="brokerElem.broker"
                :filter="true"
                :small="false"
                :value="brokerElem"
              />
            </div>
          </v-chip-group>

          <template>
            <v-row>
              <v-col cols="12" md="4" class="pr-5">
                <div class="items-flex justify-between">
                  <div>
                    {{ this.$t(`views.portfolio.positionsTable.positionNumber.${position.type}`) }}
                  </div>
                  <div class="summary-row">
                    <template v-if="!position.pendingRecreate">
                      <currency
                        :value="
                          brokerSelected ? brokerSelected.positionNumber : position.positionNumber
                        "
                        :currency="position.type === 'cash' ? position.ticker : undefined"
                        :decimals="position.type === 'crypto' ? 7 : 2"
                      />
                    </template>
                    <span v-else>-</span>
                  </div>
                </div>
                <div class="items-flex justify-between mt-1">
                  <div>
                    {{ this.$t('views.stocks.stockTable.marketPrice') }}
                  </div>
                  <div class="summary-row">
                    <PositionMarketPrice type="position" :position="position" />
                  </div>
                </div>
                <div class="items-flex justify-between mt-1">
                  <div>
                    {{ this.$t('views.stocks.stockTable.totalValue') }}
                  </div>
                  <div class="summary-row">
                    <currency
                      v-if="
                        !position.pendingRecreate &&
                        (brokerSelected ? brokerSelected.totalValue : position.totalValue)
                      "
                      :value="brokerSelected ? brokerSelected.totalValue : position.totalValue"
                      :currency="position.currency"
                    />
                    <span v-else>-</span>
                  </div>
                </div>
                <div class="items-flex justify-between mt-1">
                  <div>
                    {{
                      `${$t('views.stocks.stockTable.totalValue')} (${getCurrencySymbol(
                        user.baseCurrency
                      )})`
                    }}
                  </div>
                  <div class="summary-row">
                    <currency
                      v-if="
                        !position.pendingRecreate &&
                        (brokerSelected
                          ? brokerSelected.totalValueBaseCurrency
                          : position.totalValueBaseCurrency)
                      "
                      :value="
                        brokerSelected
                          ? brokerSelected.totalValueBaseCurrency
                          : position.totalValueBaseCurrency
                      "
                      :currency="user.baseCurrency"
                    />
                    <span v-else>-</span>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="pr-5">
                <template>
                  <div class="items-flex justify-between">
                    <div>
                      {{ $t('views.stocks.stockTable.averagePriceCost') }}
                    </div>
                    <div class="summary-row">
                      <currency
                        v-if="
                          !position.pendingRecreate &&
                          (brokerSelected
                            ? brokerSelected.averagePriceCost
                            : position.averagePriceCost)
                        "
                        :value="
                          brokerSelected
                            ? brokerSelected.averagePriceCost
                            : position.averagePriceCost
                        "
                        :currency="position.currency"
                        :decimals="3"
                      />
                      <span v-else>-</span>
                    </div>
                  </div>
                  <div class="items-flex justify-between mt-1">
                    <div>
                      {{
                        `${$t(
                          'views.stocks.stockTable.averagePriceCostWithComission'
                        )} (${getCurrencySymbol(user.baseCurrency)})`
                      }}
                    </div>
                    <div class="summary-row">
                      <currency
                        v-if="
                          !position.pendingRecreate &&
                          (brokerSelected
                            ? brokerSelected.averagePriceCostWithComissionBaseCurrency
                            : position.averagePriceCostWithComissionBaseCurrency)
                        "
                        :value="
                          brokerSelected
                            ? brokerSelected.averagePriceCostWithComissionBaseCurrency
                            : position.averagePriceCostWithComissionBaseCurrency
                        "
                        :currency="user.baseCurrency"
                        :decimals="3"
                      />
                      <span v-else>-</span>
                    </div>
                  </div>
                  <div class="items-flex justify-between mt-1">
                    <div>
                      {{ $t('views.stocks.stockTable.totalCost') }}
                    </div>
                    <div class="summary-row">
                      <currency
                        v-if="
                          !position.pendingRecreate &&
                          (brokerSelected ? brokerSelected.totalCost : position.totalCost)
                        "
                        :value="brokerSelected ? brokerSelected.totalCost : position.totalCost"
                        :currency="position.currency"
                      />
                      <span v-else>-</span>
                    </div>
                  </div>
                  <div class="items-flex justify-between mt-1">
                    <div>
                      {{
                        `${$t(
                          'views.stocks.stockTable.totalCostWithComission'
                        )} (${getCurrencySymbol(user.baseCurrency)})`
                      }}
                    </div>
                    <div class="summary-row">
                      <currency
                        v-if="
                          !position.pendingRecreate &&
                          (brokerSelected
                            ? brokerSelected.totalCostWithComissionBaseCurrency
                            : position.totalCostWithComissionBaseCurrency)
                        "
                        :value="
                          brokerSelected
                            ? brokerSelected.totalCostWithComissionBaseCurrency
                            : position.totalCostWithComissionBaseCurrency
                        "
                        :currency="user.baseCurrency"
                      />
                      <span v-else>-</span>
                    </div>
                  </div>
                </template>
              </v-col>
              <v-col cols="12" md="4" class="pr-5">
                <div v-if="!brokerSelected" class="items-flex justify-between">
                  <div>
                    {{ $t('views.stocks.stockTable.dailyPl') }}
                  </div>
                  <div class="summary-row">
                    <PositionTablePL :position="position" isDaily type="net" />
                  </div>
                </div>
                <div v-if="!brokerSelected" class="items-flex justify-between mt-1">
                  <div>
                    {{ $t('views.stocks.stockTable.dailyPlPercent') }}
                  </div>
                  <div class="summary-row">
                    <PositionTablePL :position="position" isDaily type="percent" />
                  </div>
                </div>
                <div class="items-flex justify-between mt-1">
                  <div>
                    {{
                      `${$t('views.stocks.stockTable.pl')} (${getCurrencySymbol(
                        user.baseCurrency
                      )})`
                    }}
                  </div>
                  <div class="summary-row">
                    <PositionTablePL
                      :position="brokerSelected ? brokerSelected : position"
                      type="net"
                    />
                  </div>
                </div>
                <div class="items-flex justify-between mt-1">
                  <div>
                    {{ `${this.$t('views.stocks.stockTable.plPercent')}` }}
                  </div>
                  <div class="summary-row">
                    <PositionTablePL
                      :position="brokerSelected ? brokerSelected : position"
                      type="percent"
                    />
                  </div>
                </div>
              </v-col>
            </v-row>
          </template>
        </div>
      </template>

      <div class="mt-5">
        <SummaryPortfolioPerformance ref="portfolioPerformancePl" type="pl" />
      </div>

      <div class="last-orders pl-5 py-5">
        <h1 class="card-title mb-3">
          {{ $t('views.portfolio.positionDetail.lastOrders') }}
        </h1>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th></th>
                <th>{{ $t('views.orders.ordersTable.date') }}</th>
                <th>{{ $t('views.orders.ordersTable.broker') }}</th>
                <th>{{ $t('views.orders.ordersTable.positionNumber') }}</th>
                <th>{{ $t('views.orders.ordersTable.price') }}</th>
                <th>
                  {{
                    `${$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol(
                      user.baseCurrency
                    )})`
                  }}
                </th>
              </tr>
            </thead>
            <template v-if="orders.length">
              <tbody>
                <tr v-for="item of orders">
                  <td>
                    <OrderTableTypeIcon :item="item" />
                  </td>
                  <td>
                    {{ getDateUserFormat(item.date) }}
                  </td>
                  <td>
                    <template
                      v-if="
                        [
                          'buy',
                          'sell',
                          'switch',
                          'switchBuy',
                          'spinOff',
                          'spinOffBuy',
                          'brokerTransfer',
                          'sharePremium',
                          'scrip',
                          'capitalIncrease',
                          'bonus',
                          'stakeReward',
                          'drip',
                          'optionBuy',
                          'optionSell',
                          'commission',
                          'paidInterest',
                          'interest',
                          'interestRetention',
                          'deposit',
                          'withdrawal',
                        ].indexOf(item.type) > -1
                      "
                    >
                      <TableBroker
                        :broker="item.broker"
                        :newBrokerAlias="
                          ['brokerTransfer'].indexOf(item.type) > -1
                            ? item.metadata.newBrokerAlias
                            : undefined
                        "
                      />
                    </template>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <currency
                      v-if="
                        item.positionNumber &&
                        [
                          'buy',
                          'sell',
                          'switch',
                          'switchBuy',
                          'spinOff',
                          'spinOffBuy',
                          'sharePremium',
                          'scrip',
                          'capitalIncrease',
                          'bonus',
                          'stakeReward',
                          'drip',
                          'optionBuy',
                          'optionSell',
                          'commission',
                          'paidInterest',
                          'interest',
                          'interestRetention',
                          'deposit',
                          'withdrawal',
                        ].indexOf(item.type) > -1
                      "
                      :value="item.positionNumber"
                      :currency="item.position.type === 'cash' ? item.position.ticker : undefined"
                      :decimals="7"
                      strong
                    />
                    <span v-else>-</span>
                  </td>
                  <td>
                    <currency
                      v-if="
                        [
                          'buy',
                          'sell',
                          'switch',
                          'switchBuy',
                          'sharePremium',
                          'scrip',
                          'capitalIncrease',
                          'bonus',
                          'stakeReward',
                          'drip',
                          'optionBuy',
                          'optionSell',
                          'commission',
                          'paidInterest',
                          'interest',
                          'interestRetention',
                          'deposit',
                          'withdrawal',
                        ].indexOf(item.type) > -1
                      "
                      :value="item.price"
                      :currency="item.position.currency"
                      :decimals="3"
                    />
                    <span v-else>-</span>
                  </td>
                  <td>
                    <currency
                      v-if="item.totalBaseCurrency"
                      :value="item.totalWithComissionBaseCurrency"
                      :currency="user.baseCurrency"
                      strong
                    />
                    <span v-else>-</span>
                  </td>
                </tr>
              </tbody>
            </template>
            <template v-else>
              <tbody>
                <tr>
                  <td :colspan="6" class="no-last-orders">
                    {{ $t('views.portfolio.positionDetail.noLastOrders') }}
                  </td>
                </tr>
              </tbody>
            </template>
          </template>
        </v-simple-table>
      </div>

      <div v-show="position && dividendPositionTypes.indexOf(position.type) > -1" class="mt-2 mb-5">
        <DividendsByMonth ref="dividendsByMonth" :showTable="false" :showDateTypeSelector="false" />
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from 'vuex'
import { getOrders } from '@/lib/api'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import { getDateUserFormat } from '@/lib/utils/date.js'
import { getPositionTypes } from '@/lib/positions/positions.js'
import CountryFlag from '@/views/components/common/CountryFlag'
import SummaryPortfolioPerformance from '@/views/components/dashboardDetail/summary/SummaryPortfolioPerformance'
import PositionMarketPrice from '@/views/components/common/positions/PositionMarketPrice'
import Currency from '@/views/components/common/Currency.vue'
import TableBroker from '@/views/components/common/TableBroker'
import OrderTableTypeIcon from '@/views/components/common/orders/OrderTableTypeIcon'
import TagGroup from '@/views/components/common/TagGroup'
import PositionTablePL from '@/views/components/common/positions/PositionTablePL'
import DividendsByMonth from '@/views/components/dashboardDetail/dividends/DividendsByMonth.vue'

export default {
  components: {
    CountryFlag,
    SummaryPortfolioPerformance,
    PositionMarketPrice,
    Currency,
    TableBroker,
    OrderTableTypeIcon,
    TagGroup,
    PositionTablePL,
    DividendsByMonth,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    loading: false,
    orders: [],
    brokerSelected: null,
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    dividendPositionTypes() {
      return getPositionTypes({ model: 'dividendPositionTypes' })
    },
    positionBrokers() {
      if (!this.position.brokers || !this.position.brokers.length) return []
      return this.position.brokers.filter((elem) => elem.positionNumber !== 0)
    },
  },
  watch: {
    active: {
      handler() {
        if (this.active) this.init()
      },
      immediate: true,
    },
  },
  methods: {
    getDateUserFormat,
    getCurrencySymbol,
    inputModal(value) {
      if (value === false) this.closeModal()
    },
    closeModal() {
      this.brokerSelected = null

      this.$emit('update:active', false)
    },
    async init() {
      if (!this.position) return

      if (this.$refs.portfolioPerformancePl)
        this.$refs.portfolioPerformancePl.load({ filters: { _id: this.position._id } })
      if (this.$refs.dividendsByMonth)
        this.$refs.dividendsByMonth.loadSummary({ position: [this.position._id] })

      const resultOrders = await getOrders({
        limit: 5,
        page: 1,
        filter: {
          isFxOrder: { $ne: true },
          position: this.position._id,
        },
        sort: { date: -1 },
      })
      this.orders = resultOrders.data.docs
    },
    editPosition() {
      this.$emit('editPosition', this.position)
      this.closeModal()
    },
  },
}
</script>

<style lang="scss">
#position-detail-drawer {
  .items-flex {
    display: flex;
    &.justify-between {
      justify-content: space-between;
    }
    &.wrap {
      flex-wrap: wrap;
    }
  }
  h1.card-title {
    font-size: 15px;
  }
  h1.position-detail-drawer-ticker {
    font-size: 23px;
    font-weight: 700;
  }

  .summary-row {
    font-weight: 500;
  }
  .drawer-bold {
    font-weight: 500;
  }
  .position-detail-drawer-tags {
    margin-bottom: 2px;
  }
  .position-detail-drawer-h2 {
    margin-left: 1px;
    margin-top: 2px;
    font-size: 16px;
    font-weight: 400;
  }
  .position-detail-drawer-notes {
    font-style: italic;
  }
  .last-orders {
    td,
    th {
      text-align: center;
      #table-broker {
        justify-content: center;
      }
    }
    .no-last-orders {
      text-align: center;
      color: #636363;
    }
  }
}
</style>
